import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { QuoteProvider } from "./contexts/QuoteContext";
import Layout from "./components/Layout";

// Pages
import Login from "./pages/Login";
import CustomerDetails from "./pages/CustomerDetails";
import SelectPack from "./pages/SelectPack";
import ReviewQuote from "./pages/ReviewQuote";
import QuoteSuccess from "./pages/QuoteSuccess";
import MyQuotes from "./pages/MyQuotes";
import AdminDashboard from "./pages/admin/AdminDashboard";
import SparesManagement from "./pages/admin/SparesManagement";
import CustomersManagement from "./pages/admin/CustomersManagement";
import QuotesManagement from "./pages/admin/QuotesManagement";
import EmailTemplates from "./pages/admin/EmailTemplates";
import QuoteSettings from "./pages/admin/QuoteSettings";
import AdminSettings from "./pages/admin/AdminSettings";
import AppSettings from "./pages/admin/AppSettings";
import VatRatesManagement from "./pages/admin/VatRatesManagement";
import LogsPage from "./pages/admin/LogsPage";
import ReportDesigner from "./pages/admin/ReportDesigner";
import EngineerHistory from "./pages/admin/EngineerHistory";
import UserManagement from "./pages/admin/UserManagement";

// Protected Route Component
const ProtectedRoute = ({ children, requireAdmin = false, requireGlobalAdmin = false }) => {
    const { isAuthenticated, loading, isAdmin, isGlobalAdmin } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="spinner" />
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    if (requireGlobalAdmin && !isGlobalAdmin()) {
        return <Navigate to="/admin" replace />;
    }

    if (requireAdmin && !isAdmin()) {
        return <Navigate to="/" replace />;
    }

    return children;
};

// Engineer Route - redirects admins to admin dashboard
const EngineerRoute = ({ children }) => {
    const { isAuthenticated, loading, isAdmin } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="spinner" />
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    if (isAdmin()) {
        return <Navigate to="/admin" replace />;
    }

    return children;
};

// Public Route - redirects authenticated users
const PublicRoute = ({ children }) => {
    const { isAuthenticated, loading, isAdmin } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="spinner" />
            </div>
        );
    }

    if (isAuthenticated) {
        return <Navigate to={isAdmin() ? "/admin" : "/"} replace />;
    }

    return children;
};

function AppRoutes() {
    return (
        <Routes>
            {/* Public Routes */}
            <Route
                path="/login"
                element={
                    <PublicRoute>
                        <Login />
                    </PublicRoute>
                }
            />

            {/* Engineer Routes */}
            <Route
                path="/"
                element={
                    <EngineerRoute>
                        <Layout>
                            <CustomerDetails />
                        </Layout>
                    </EngineerRoute>
                }
            />
            <Route
                path="/select-pack"
                element={
                    <EngineerRoute>
                        <Layout>
                            <SelectPack />
                        </Layout>
                    </EngineerRoute>
                }
            />
            <Route
                path="/review"
                element={
                    <EngineerRoute>
                        <Layout>
                            <ReviewQuote />
                        </Layout>
                    </EngineerRoute>
                }
            />
            <Route
                path="/success"
                element={
                    <EngineerRoute>
                        <Layout>
                            <QuoteSuccess />
                        </Layout>
                    </EngineerRoute>
                }
            />
            <Route
                path="/quotes"
                element={
                    <EngineerRoute>
                        <Layout>
                            <MyQuotes />
                        </Layout>
                    </EngineerRoute>
                }
            />

            {/* Admin Routes */}
            <Route
                path="/admin"
                element={
                    <ProtectedRoute requireAdmin>
                        <Layout>
                            <AdminDashboard />
                        </Layout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/spares"
                element={
                    <ProtectedRoute requireAdmin>
                        <Layout>
                            <SparesManagement />
                        </Layout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/customers"
                element={
                    <ProtectedRoute requireAdmin>
                        <Layout>
                            <CustomersManagement />
                        </Layout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/quotes"
                element={
                    <ProtectedRoute requireAdmin>
                        <Layout>
                            <QuotesManagement />
                        </Layout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/email-templates"
                element={
                    <ProtectedRoute requireAdmin>
                        <Layout>
                            <EmailTemplates />
                        </Layout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/quote-settings"
                element={
                    <ProtectedRoute requireAdmin>
                        <Layout>
                            <QuoteSettings />
                        </Layout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/settings"
                element={
                    <ProtectedRoute requireAdmin>
                        <Layout>
                            <AdminSettings />
                        </Layout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/app-settings"
                element={
                    <ProtectedRoute requireGlobalAdmin>
                        <Layout>
                            <AppSettings />
                        </Layout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/vat-rates"
                element={
                    <ProtectedRoute requireAdmin>
                        <Layout>
                            <VatRatesManagement />
                        </Layout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/logs"
                element={
                    <ProtectedRoute requireGlobalAdmin>
                        <Layout>
                            <LogsPage />
                        </Layout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/report-designer"
                element={
                    <ProtectedRoute requireGlobalAdmin>
                        <Layout>
                            <ReportDesigner />
                        </Layout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/engineer-history"
                element={
                    <ProtectedRoute requireAdmin>
                        <Layout>
                            <EngineerHistory />
                        </Layout>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/admin/users"
                element={
                    <ProtectedRoute requireAdmin>
                        <Layout>
                            <UserManagement />
                        </Layout>
                    </ProtectedRoute>
                }
            />

            {/* Catch all - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <AuthProvider>
            <QuoteProvider>
                <BrowserRouter>
                    <AppRoutes />
                    <Toaster 
                        position="top-center" 
                        richColors 
                        toastOptions={{
                            className: 'font-body',
                            duration: 3000
                        }}
                    />
                </BrowserRouter>
            </QuoteProvider>
        </AuthProvider>
    );
}

export default App;
